import { SwitchMode } from '../../../types/backend';
import type { SwitchState } from '../../../types/backend';

// What the switch editor holds, and how it maps onto what ops.SetFeatureSwitch takes. The server
// validates every change again; this only decides what the form offers.

export type EnabledFor = typeof SwitchMode.GROUPS | typeof SwitchMode.EVERYONE;

export interface SwitchForm {
  enabled: boolean;
  enabledFor: EnabledFor;
  groups: string[];
  /** Background work (automation rules, reconcilers) beside the selected groups; everyone already includes it. */
  background: boolean;
  note: string;
}

export type SwitchFormErrors = Partial<Record<'groups', string>>;

/** The choices for "Enabled for", in the order the select shows them. */
export const ENABLED_FOR_OPTIONS: { value: EnabledFor; label: string }[] = [
  { value: SwitchMode.GROUPS, label: 'Selected groups' },
  { value: SwitchMode.EVERYONE, label: 'Everyone' },
];

export const BACKGROUND_LABEL = 'Background work (automation rules, reconcilers)';

export function formFromState(state: SwitchState): SwitchForm {
  return {
    enabled: state.mode !== SwitchMode.OFF,
    enabledFor: state.mode === SwitchMode.EVERYONE ? SwitchMode.EVERYONE : SwitchMode.GROUPS,
    groups: [...state.groups],
    background: state.background,
    note: '',
  };
}

export function stateFromForm(form: SwitchForm): SwitchState {
  if (!form.enabled) return { mode: SwitchMode.OFF, groups: [], background: false };
  if (form.enabledFor === SwitchMode.EVERYONE) return { mode: SwitchMode.EVERYONE, groups: [], background: false };
  return { mode: SwitchMode.GROUPS, groups: form.groups, background: form.background };
}

export function validateSwitchForm(form: SwitchForm): SwitchFormErrors {
  if (form.enabled && form.enabledFor === SwitchMode.GROUPS && form.groups.length === 0) {
    return { groups: 'Pick at least one group' };
  }
  return {};
}

/** The same switch state; the order groups were picked in does not matter. */
export function sameState(a: SwitchState, b: SwitchState): boolean {
  const sorted = (x: string[]) => [...x].sort();
  const [x, y] = [sorted(a.groups), sorted(b.groups)];
  return a.mode === b.mode && a.background === b.background
    && x.length === y.length && x.every((v, i) => v === y[i]);
}

/** The groups to offer: those the server offers, plus any the switch holds that it no longer does. */
export function groupOptions(available: string[], current: SwitchState): string[] {
  return [...available, ...current.groups.filter((g) => !available.includes(g))];
}

function list(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Who a state gives the feature to, short enough for a list row; background work is marked apart. */
export function summarizeState(state: SwitchState): string {
  switch (state.mode) {
    case SwitchMode.OFF:
      return 'Off';
    case SwitchMode.EVERYONE:
      return 'Everyone';
    default:
      return state.groups.length === 1 ? state.groups[0] : `${state.groups.length} groups`;
  }
}

/** Who a state gives the feature to, in the words the panel uses. */
export function describeState(state: SwitchState): string {
  switch (state.mode) {
    case SwitchMode.OFF:
      return 'Off';
    case SwitchMode.EVERYONE:
      return 'Everyone';
    default:
      return state.background ? list([...state.groups, 'background work']) : list(state.groups);
  }
}
